/**
 * Map a getUserMedia / react-webcam error to a human-readable message
 * the candidate can act on. The argument can be a DOMException, an
 * Error, or a string (react-webcam may pass any of these to
 * onUserMediaError).
 */
export function getCameraErrorMessage(error) {
  const name =
    (error && typeof error === "object" && "name" in error && error.name) ||
    (typeof error === "string" ? error : "");

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError": // older Safari / Chromium
      return 'Camera permission is blocked. Please allow camera access in your browser settings, then click "Try again".';
    case "NotFoundError":
    case "DevicesNotFoundError":
      return 'No camera was detected on this device. Please connect a camera and click "Try again".';
    case "NotReadableError":
    case "TrackStartError":
      return 'Your camera appears to be in use by another application. Please close other apps using the camera and click "Try again".';
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return 'Your camera does not meet the required settings. Try a different camera and click "Try again".';
    case "SecurityError":
      return "Camera access is blocked by your browser's security settings.";
    case "AbortError":
      return 'Camera access was interrupted. Please click "Try again".';
    default:
      return 'We couldn\'t access your camera. Please check your camera and browser settings, then click "Try again".';
  }
}

/**
 * Same mapping for microphone access errors. Same DOMException names
 * apply (getUserMedia uses one error taxonomy).
 */
export function getMicrophoneErrorMessage(error) {
  const name =
    (error && typeof error === "object" && "name" in error && error.name) ||
    (typeof error === "string" ? error : "");

  switch (name) {
    case "NotAllowedError":
    case "PermissionDeniedError":
      return 'Microphone permission is blocked. Please allow microphone access in your browser settings, then click "Check Microphone" again.';
    case "NotFoundError":
    case "DevicesNotFoundError":
      return 'No microphone was detected on this device. Please connect a microphone and try again.';
    case "NotReadableError":
    case "TrackStartError":
      return 'Your microphone appears to be in use by another application. Please close other apps using the microphone and try again.';
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return 'Your microphone does not meet the required settings. Try a different microphone.';
    case "SecurityError":
      return "Microphone access is blocked by your browser's security settings.";
    case "AbortError":
      return "Microphone access was interrupted. Please try again.";
    default:
      return "We couldn't access your microphone. Please check your microphone and browser settings, then try again.";
  }
}
